import { useRef, useState, useMemo, useEffect } from 'react';
import { MultiSelect, Option } from 'react-multi-select-component';
import { useDebouncedAbortableFetch } from 'scripts/hooks/useDebouncedAbortableFetch';
import { captureException } from 'scripts/logging/error-report';
import { apiBaseUrl } from 'utils/constants';

type SearchType = 'city' | 'zip';

interface Props {
  searchType: SearchType;
  value: Option[];
  onChange: (items: Option[]) => void;
  placeholder?: string;
  overrideStrings?: { [key: string]: string };
}

interface ICommon {
  myval: string;
  mymulti: string;
  mytype: string;
}

interface IData {
  data: ICommon[];
}

export function AsyncMultiSelect({ searchType, value, onChange, placeholder = 'Type to search…', overrideStrings }: Props) {
  const [options, setOptions] = useState<Option[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const debouncedFetch = useDebouncedAbortableFetch(350);

  const cache = useRef<Map<string, Option[]>>(new Map());
  const lastRef = useRef('');                 // last search term seen by filterOptions
  const lastFetchedOptions = useRef<Option[]>([]); // keep last fetched options

  // Helper function to merge selected values with base options
  const mergeSelectedWithOptions = (baseOptions: Option[], selectedValues: Option[]): Option[] => {
    if (!selectedValues || selectedValues.length === 0) {
      return baseOptions;
    }

    const mergedOptions = [...baseOptions];
    const existingValues = new Set(baseOptions.map(opt => opt.value));

    selectedValues.forEach(selected => {
      if (!existingValues.has(selected.value)) {
        mergedOptions.push(selected); // Add selected items at the end
        existingValues.add(selected.value);
      }
    });

    return mergedOptions;
  };

  // Ensure selected values are always in options
  useEffect(() => {
    if (value && value.length > 0) {
      setOptions(prevOptions => mergeSelectedWithOptions(prevOptions, value));
    } else if (value && value.length === 0) {
      // When value is cleared (empty array), reset options to last fetched or empty
      setOptions(lastFetchedOptions.current);
    }
  }, [value]);

  const load = (term: string, signal: AbortSignal) => {
    /* 1️⃣  Skip tiny queries - but keep last search results visible */
    if (term.trim().length < 1) {
      setIsLoading(false);
      // Show last fetched options merged with selected values when search is empty
      setOptions(mergeSelectedWithOptions(lastFetchedOptions.current, value));
      return;
    }

    /* 2️⃣  Skip if we already have it */
    if (cache.current.has(term)) {
      setIsLoading(false);
      const cachedOptions = cache.current.get(term)!;

      // Store only fetched options, not merged ones
      lastFetchedOptions.current = cachedOptions;

      // Merge and set options
      setOptions(mergeSelectedWithOptions(cachedOptions, value));
      return;
    }

    /* 3️⃣  Otherwise fetch & cache */
    setIsLoading(true);
    const url =
      `${searchType === 'city' ? 'city' : 'zipcode'}-typeahead?searchterm=${encodeURIComponent(term)}`;

    fetch(`${apiBaseUrl}/api/v1/chatbot/${url}`, { signal })
      .then(r => r.json())
      .then((data: IData) => {
        const list = data?.data?.map((x: any) => ({ value: String(x?.myval), label: String(x?.myval) })) ?? [];
        cache.current.set(term, list);

        // Remember results
        lastFetchedOptions.current = list;

        // Merge and set options
        setOptions(mergeSelectedWithOptions(list, value));
        setIsLoading(false);
      })
      .catch((e) => {
        setIsLoading(false);
        if (e.name !== 'AbortError') {
          captureException(e);
          console.error(`Error fetching ${searchType} data:`, e);
        }
      });
  };

  /* 4️⃣  Debounce **and** ignore duplicates */
  const filterOptions = (_: Option[], search: string) => {
    // When dropdown opens without search, show only selected items
    if (search.trim().length < 1 && value?.length > 0) {
      return value;
    }

    if (search === lastRef.current) return options;   // same term → do nothing
    lastRef.current = search;                         // remember new term
    debouncedFetch(search, load);                     // debounce network call

    // If search is empty, show last fetched options merged with currently selected values
    if (search.trim().length < 1) {
      return mergeSelectedWithOptions(lastFetchedOptions.current, value);
    }

    return options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()));
  };


  const defaultOverrideStrings = useMemo(
    () => ({
      selectSomeItems: placeholder,
      search: `Search ${searchType}…`,
      allItemsAreSelected: 'All selected',
      noOptions: isLoading ? "Fetching results..." : "No matches found"
    }),
    [placeholder, searchType, isLoading]
  );
  const customValueRenderer = (selected: Option[], _options: Option[]) => {
    if (selected.length === 0) {
      return placeholder;
    }

    return selected.map(s => s.label).join(', ');
  };

  const handleChange = (selected: Option[]) => {
    // If cleared (empty array), reset everything
    if (selected.length === 0) {
      setOptions([]);
      lastFetchedOptions.current = [];
      lastRef.current = '';
    }
    onChange(selected);
  };

  return (
    <MultiSelect
      labelledBy="Select"
      options={options}
      value={value}
      onChange={handleChange}
      hasSelectAll={false}
      filterOptions={filterOptions}
      overrideStrings={overrideStrings ? { ...defaultOverrideStrings, ...overrideStrings } : defaultOverrideStrings}
      valueRenderer={customValueRenderer}
      className='mb-3'
    />
  );
}
